import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import {
  GetSentInviteLinkHistoryRequest,
  SendUserInviteSMSRequest,
  SendUserInviteSMSResponse,
  GetSentInviteLinkHistoryResponse,
  GetSentInviteLinkHistoryResponseRow,
  GetLocationHierarchyRequest,
  GetLocationHierarchyResponse,
  GetHistoryForOnboardingActivityRequest,
  GetHistoryForOnboardingActivityResponse,
} from './onboarding.types';
import { onboardingFastPassApi } from './onboarding.api';
import { AxiosError } from 'axios';
import { OnboardingEventHistoryService } from '@weave/schema-gen-ts/dist/schemas/insys/onboarding/v1/onboarding_history/onboarding_history.pb';
import { authorizedFetch } from 'helpers/utils';
import {
  InsysOnboardingIntakeFormAPI,
  SendUserInviteEmailRequest,
  SendUserInviteEmailResponse,
} from '@weave/schema-gen-ts/dist/schemas/insys/intake-form/v1/insys_onboarding_intake_form.pb';

export const OnboardingQueryKeys = {
  baseQueryKeys: ['onboarding'],
  getSentInviteLinkHistory: (request: GetSentInviteLinkHistoryRequest) => [
    ...OnboardingQueryKeys.baseQueryKeys,
    'fastPass',
    'getSentInviteLinkHistory',
    request.locationId,
  ],
  getFastPassOnboardingActivity: (request: GetHistoryForOnboardingActivityRequest) => [
    ...OnboardingQueryKeys.baseQueryKeys,
    'fastPass',
    'getOnboardingActivity',
    request.slug,
  ],
  getLocationHierarchy: (request: GetLocationHierarchyRequest) => [
    ...OnboardingQueryKeys.baseQueryKeys,
    'getLocationHierarchy',
    request.locationId,
  ],
};

export const useSendUserInviteSms = (
  options?: UseMutationOptions<
    SendUserInviteSMSResponse,
    AxiosError<any>,
    SendUserInviteSMSRequest
  >
) => {
  return useMutation({
    mutationFn: onboardingFastPassApi.sendUserInviteSms,
    ...options,
  });
};

export const useSendUserInviteEmail = (
  options?: UseMutationOptions<
    SendUserInviteEmailResponse,
    AxiosError<any>,
    SendUserInviteEmailRequest
  >
) => {
  return useMutation({
    mutationFn: (request: SendUserInviteEmailRequest) =>
      InsysOnboardingIntakeFormAPI.SendUserInviteEmail(authorizedFetch(), request),
    ...options,
  });
};

export const useGetSentInviteLinkHistory = (
  request: GetSentInviteLinkHistoryRequest,
  options?: UseQueryOptions<
    GetSentInviteLinkHistoryResponse,
    AxiosError<any>,
    GetSentInviteLinkHistoryResponseRow[]
  >
) => {
  return useQuery({
    queryKey: OnboardingQueryKeys.getSentInviteLinkHistory(request),
    queryFn: async () => await onboardingFastPassApi.getSentInviteLinkHistory(request),
    select: (data) => data?.response || [],
    ...options,
    retry: false,
    enabled: !!request.locationId && (options?.enabled ?? true),
  });
};

export const useGetFastPassOnboardingActivity = <
  TData = GetHistoryForOnboardingActivityResponse
>(
  request: GetHistoryForOnboardingActivityRequest,
  options?: UseQueryOptions<
    GetHistoryForOnboardingActivityResponse,
    AxiosError<any>,
    TData
  >
) => {
  return useQuery({
    queryKey: OnboardingQueryKeys.getFastPassOnboardingActivity(request),
    queryFn: async () =>
      await OnboardingEventHistoryService.GetHistoryForOnboardingActivity(
        authorizedFetch(),
        request
      ),
    ...options,
    enabled: !!request.slug && (options?.enabled ?? true),
  });
};

export const useGetLocationHierarchy = <TData = GetLocationHierarchyResponse>(
  request: GetLocationHierarchyRequest,
  options: UseQueryOptions<GetLocationHierarchyResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    queryKey: OnboardingQueryKeys.getLocationHierarchy(request),
    queryFn: () =>
      InsysOnboardingIntakeFormAPI.GetLocationHierarchy(authorizedFetch(), request),
    ...options,
    enabled: !!request.locationId && (options?.enabled ?? true),
  });
};
